import {
  PhoneVerifyRequestTypeEnum, UserDtoLanguageEnum, UserDtoTypeEnum, UsersApi,
  UserUpdateRequest,
} from '@coretava/api/core-service';
import { Countries } from '@gamiphy/bl/country';
import {
  RegisterUserRequestWithHash,
  SocialConnectData,
  UpgradeUserRequest,
  UserApi,
  WsUser,
} from '@gamiphy/gamix-modules/user';
import { UserWithMetadata } from '@gamiphy/gamix-modules/user/types';
import { Http } from '@gamiphy/http';
import { getLanguage } from '@coretava/i18n';
import { UsersAnalytics } from '@gamiphy/loyalty-station-desktop/store/analytics';
import { AppStore } from '@gamiphy/loyalty-station-desktop/store/app';
import {
  clearLocalStorage,
  getLocalStorageItem,
  removeLocalStorageItem,
  setLocalStorageItem,
} from '@gamiphy/utils/storage';
import axios, { isAxiosError } from 'axios';
import unescape from 'lodash/unescape';
import {
  action, computed, makeAutoObservable, observable, runInAction, when,
} from 'mobx';
import { v4 as uuidv4 } from 'uuid';
import { ThemeStore } from '../theme';
import { URL_PARAMS } from '../ui/constants';
import {
  getTokenLocalStorageKey, REFERRAL_QUERY_PARAMS, REFERRER_LOCAL_STORAGE, WsEvents,
} from './constants';
import { UserInit } from './types';

class UserStore {
  @observable initialized = false;

  @observable loading = false;

  @observable user?: UserWithMetadata = undefined;

  @observable token?: string = undefined;

  @observable unverifiedName?: string = undefined;

  @observable referrer?: string | null = getLocalStorageItem(REFERRER_LOCAL_STORAGE);

  constructor() {
    makeAutoObservable(this);

    void this.initialize();
  }

  @computed
  get api(): UsersApi {
    return new UsersApi({
      isJsonMime(): boolean {
        return true;
      },
      basePath: process.env.BASE_API_URL,
      accessToken: this.token,
    });
  }

  @computed get isLoggedIn() {
    return !!this.user;
  }

  @computed get isDraftUser(): boolean {
    return !!this.user && this.user.type === UserDtoTypeEnum.Draft;
  }

  @computed get isRealUser(): boolean {
    return !!this.user && this.user.type === UserDtoTypeEnum.Member;
  }

  @computed get isPristine(): boolean {
    return !!this.user && !this.user.points.gems;
  }

  @computed get prefLang(): UserDtoLanguageEnum | undefined {
    return this.user?.language;
  }

  @computed get country(): Countries {
    return (this.user?.country || AppStore.app.country) as Countries;
  }

  @computed get isHowToLevelUpAvailable(): boolean {
    return !(this.user?.metadata?.howToLevelUpCompleted ?? false);
  }

  @computed get isHowToEarnDiscountAvailable(): boolean {
    return !(this.user?.metadata?.howToEarnDiscountCompleted ?? false);
  }

  @computed get currentYearBirthDate(): Date | undefined {
    return this.user?.dateOfBirth
      ? new Date(new Date().getFullYear(), (this.user.dateOfBirth?.month ?? 0) - 1, this.user.dateOfBirth.day)
      : undefined;
  }

  @computed get isCooperado(): boolean {
    return this.user?.metadata?.isCooperado?.toLowerCase() === 'true';
  }

  @action initialize(): void {
    when(() => this.isLoggedIn, () => {
      if (this.user) {
        void this.wsConnect(this.user.app, this.user.id);
        void this.verifyCooperadoMemberShip(this.user.email ?? undefined);
      }
    });

    when(() => AppStore.initialized && this.isRealUser, () => {
      // Clean local storage when real user logged in
      removeLocalStorageItem(REFERRER_LOCAL_STORAGE);
    });

    when(() => AppStore.initialized && this.isDraftUser, () => {
      // Set user and token for draft user
      if (this.user && this.token) {
        setLocalStorageItem(getTokenLocalStorageKey(AppStore.app.id), this.token);
      }
    });
  }

  @action setCredentials(token: string): void {
    Http.setAuthorization(token);
    this.token = token;
  }

  @action
  async register(userObject: RegisterUserRequestWithHash) {
    const {
      user,
      token,
      isNew,
    } = await UserApi.register({
      language: getLanguage(),
      ...userObject,
      referrer: this.referrer ?? undefined,
    });

    this.setCredentials(token);
    runInAction(() => {
      this.user = user;
    });

    if (isNew) {
      UsersAnalytics.trackSignedUp();
    } else {
      UsersAnalytics.trackLoggedIn();
    }
  }

  @action
  async registerWithHashAndToken(userObject: RegisterUserRequestWithHash) {
    const {
      user,
      token,
      isNew,
    } = await UserApi.registerWithToken({
      language: getLanguage(),
      ...userObject,
      referrer: this.referrer ?? undefined,
    });

    this.setCredentials(token);
    runInAction(() => {
      this.user = user;
    });

    if (isNew) {
      UsersAnalytics.trackSignedUp();
    } else {
      UsersAnalytics.trackLoggedIn();
    }
  }

  @action
  setUser(user: UserWithMetadata): void {
    this.user = user;
  }

  @action
  setUnverifiedName(name?: string): void {
    this.unverifiedName = name;
  }

  @action
  async verifyCooperadoMemberShip(email?: string): Promise<void> {
    await when(() => this.initialized);

    if (window.location.hostname.includes('coopdrogaria.com.br')) {
      const response = await axios.post<{ isCooperado: boolean }>('/verifycoop', { email }, {
        headers: {
          accept: 'application/json',
          'content-type': 'application/json',
        },
      });
      if (this.isCooperado !== response.data.isCooperado) {
        await this.update({ metadata: { isCooperado: response.data.isCooperado.toString() } });
      }
    }
  }

  @action
  async update(data: Partial<UserUpdateRequest>): Promise<void> {
    if (this.user) {
      const response = await this.api.usersControllerUpdate(AppStore.app.id, data);
      const updatedUser = response.data.user;
      this.user = {
        ...updatedUser,
        metadata: updatedUser.metadata || {},
      };
    }
  }

  @action
  async upgrade(userObject: UpgradeUserRequest) {
    this.loading = true;

    const {
      user,
      token,
      isNew,
    } = await UserApi.upgrade(userObject);
    this.setCredentials(token);
    this.user = {
      ...user,
      metadata: user.metadata || {},
    };

    this.loading = false;

    if (isNew) {
      UsersAnalytics.trackSignedUp();
    } else {
      UsersAnalytics.trackLoggedIn();
    }
  }

  @action
  async registerDraftUser(data: { email?: string, firstName?: string } = {}) {
    await when(() => AppStore.initialized);

    const {
      user,
      token,
    } = await UserApi.register({
      firstName: data.firstName,
      password: uuidv4(),
      type: UserDtoTypeEnum.Draft,
      referrer: this.referrer ?? undefined,
      language: getLanguage(),
      ...(data.email ? {
        email: data.email,
      } : {
        id: uuidv4(),
      }),
    });

    this.setCredentials(token);
    this.user = user;
  }

  @action
  async validateToken(token: string): Promise<UserWithMetadata | undefined> {
    try {
      const user = await UserApi.authorizeUser(token);

      this.setCredentials(token);

      return user;
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.status && [401, 403].includes(e.response?.status)) {
        removeLocalStorageItem(getTokenLocalStorageKey(AppStore.app.id));

        return undefined;
      }
      throw e;
    }
  }

  @action
  async login(user?: UserInit): Promise<void> {
    // Wait for app store to be initialized
    await when(() => AppStore.initialized && ThemeStore.initialized);

    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get(URL_PARAMS.code);
    const userId = urlParams.get(URL_PARAMS.userId);

    const token = getLocalStorageItem(getTokenLocalStorageKey(AppStore.app.id));
    const tokenUser = token ? await this.validateToken(token) : undefined;

    // Read referral data if any
    this.readReferrerData();

    if (user) {
      const userData = {
        ...user,
        firstName: user.firstName ? unescape(user.firstName) : undefined,
        lastName: user.lastName ? unescape(user.lastName) : undefined,
      };
      if (tokenUser && tokenUser.type === UserDtoTypeEnum.Member) {
        await this.registerWithHashAndToken({
          ...userData,
          type: UserDtoTypeEnum.Member,
        });
      } else if (tokenUser && !('id' in user)) {
        await this.upgrade(user);
      } else {
        await this.register({
          ...userData,
          type: UserDtoTypeEnum.Member,
        });
      }

      removeLocalStorageItem(getTokenLocalStorageKey(AppStore.app.id));
    } else if (tokenUser) {
      runInAction(() => {
        this.user = tokenUser;
      });
    } else if (code && userId) {
      await this.createAndAutoVerifyUser(userId, code);
    }

    runInAction(() => {
      this.initialized = true;
    });
  }

  @action logout(): void {
    clearLocalStorage();
    this.user = undefined;
    this.token = undefined;
    window.location.reload();
  }

  @action
  async reload(): Promise<void> {
    if (this.user) {
      this.user = await UserApi.getUser(this.user.id);
    }
  }

  @action
  async socialConnect(social: SocialConnectData) {
    if (this.user) {
      this.user = await UserApi.connectUser(social);
    }
  }

  @action
  async updatePhone(phone: string): Promise<void> {
    if (this.user) {
      const user = await UserApi.updatePhone(phone);
      runInAction(() => {
        this.user = user;
      });
    }
  }

  @action
  async verifyPhone(phone: string, otp: string): Promise<void> {
    if (this.user) {
      const {
        user,
        token,
      } = await UserApi.verifyPhoneOtp(phone, otp);
      runInAction(() => {
        this.user = user;
        this.setCredentials(token);
      });
    }
  }

  @action
  async createAndVerifyUser(data: {
    contact: string,
    otp: string,
    name?: string,
    type: PhoneVerifyRequestTypeEnum
  }): Promise<void> {
    const response = await this.api.usersVerificationsControllerVerifyPhone(
      AppStore.app.id,
      {
        contact: data.contact,
        code: data.otp,
        name: data.name,
        type: data.type,
      },
    );

    this.setCredentials(response.data.token);

    setLocalStorageItem(getTokenLocalStorageKey(AppStore.app.id), response.data.token);
    runInAction(() => {
      this.user = {
        ...response.data.user,
        metadata: response.data.user.metadata || {},
      };
    });
  }

  @action
  async createAndAutoVerifyUser(contact: string, otp: string): Promise<void> {
    try {
      await this.createAndVerifyUser({
        contact,
        otp,
        type: PhoneVerifyRequestTypeEnum.UserId,
      });
    } catch (e) {
      if (isAxiosError(e)) {
        switch (e.response?.status) {
          case 400:
            // Do nothing
            break;
          default:
            throw e;
        }
      }
    }
  }

  @action
  private readReferrerData(): void {
    const urlParams = new URLSearchParams(window.location.search);

    const appId = urlParams.get(REFERRAL_QUERY_PARAMS.GAMIPHY_APP_ID);
    const userId = urlParams.get(REFERRAL_QUERY_PARAMS.GAMIPHY_USER_ID);

    if (appId && userId && AppStore.app.id === appId) {
      setLocalStorageItem(REFERRER_LOCAL_STORAGE, userId);
      this.referrer = userId;
    }
  }

  @action
  private async wsConnect(appId: string, userId: string): Promise<void> {
    const io = await import('socket.io-client');
    const socket = io.default(Http.getBaseUrl() || '', {
      path: '/core/ws',
      reconnectionAttempts: 2,
      transports: ['websocket'],
    });
    socket.on('connect', () => socket.emit('join', {
      appId,
      userId,
    }));
    socket.on(WsEvents.userUpdated, (data: WsUser) => {
      this.user = {
        ...this.user,
        ...data.data,
      };
    });
  }
}

export default new UserStore();
